import axios from "axios";

// Feature keys imply their page: a hat that grants any invoices feature
// (e.g. add_invoice.access) must also open the Invoices page, otherwise the
// grant is unreachable. Keyed by route.* key; values are feature-key
// prefixes that imply it. Pages without feature keys (route.admin_settings,
// route.user_accounts, …) stay explicit-only.
const PAGE_IMPLICATIONS = {
  "route.dashboard": ["dashboard."],
  "route.accounts": ["accounts."],
  "route.units": ["units."],
  "route.invoices": ["invoices.", "add_invoice.", "down_payments."],
  "route.quotations": ["quotations."],
  "route.customer_purchase_orders": ["customer_po."],
  "route.internal_purchase_orders": ["internal_po."],
  "route.supplier_purchase_orders": ["supplier_po."],
  "route.items": ["items."],
  "route.rma": ["rma.", "appbar.returned_items"],
  "route.services": ["services."],
  "route.suppliers": ["suppliers."],
  "route.employees": ["employees."],
  "route.journal_entries": ["accounting."],
  "route.inventory_reports": ["reports."],
  "route.service_reports": ["reports."],
  "route.leads": ["leads."],
  "route.sales_dashboard": ["leads."],
};

const state = {
  permissions: [],
  user_permissions: [],
  // Distinguishes "permissions not fetched yet" from "fetched, but the
  // account has no hats" — an empty list after load means NO access.
  user_permissions_loaded: false,
  hats: [],
};

const getters = {
  permissions: (state) => state.permissions,
  user_permissions: (state) => state.user_permissions,
  user_permissions_loaded: (state) => state.user_permissions_loaded,
  hasPermission: (state) => (key) => {
    if (state.user_permissions.includes(key)) return true;
    const impliedBy = PAGE_IMPLICATIONS[key];
    return (
      Array.isArray(impliedBy) &&
      state.user_permissions.some((p) =>
        impliedBy.some((prefix) => p.startsWith(prefix))
      )
    );
  },
  hats: (state) => state.hats,
};

const actions = {
  async fetch_permissions({ commit }) {
    const response = await axios.get("/permissions");
    commit("SET_PERMISSIONS", response.data);
    return response.data;
  },

  async fetch_user_permissions({ commit }) {
    const response = await axios.get("/permissions/user");
    commit("SET_USER_PERMISSIONS", response.data);
    return response.data;
  },

  // Route guards call this before checking keys so a hard refresh on a
  // guarded page still has the user's (hat-based) permissions loaded.
  async ensure_user_permissions({ state, dispatch }) {
    if (state.user_permissions_loaded) {
      return state.user_permissions;
    }
    return dispatch("fetch_user_permissions");
  },

  // Global kill-switch: flag/unflag a permission as disabled for ALL accounts.
  // When disabled, the server strips the key from every account's effective
  // set (/permissions/user) regardless of their hats.
  async set_global_disable({ commit, dispatch }, { id, disabled }) {
    const response = await axios.put("/permissions/" + id + "/global_disable", {
      disabled,
    });
    commit("UPSERT_PERMISSION", response.data);
    // Re-pull the current user's effective keys so the switch takes effect
    // immediately in this session (e.g. the feature's button disappears now).
    await dispatch("fetch_user_permissions");
    return response.data;
  },

  async seed_permissions() {
    const response = await axios.post("/permissions/seed");
    return response.data;
  },

  async fetch_hats({ commit }) {
    const response = await axios.get("/hats");
    commit("SET_HATS", response.data);
    return response.data;
  },

  async create_hat({ commit }, payload) {
    const response = await axios.post("/hats", payload);
    commit("UPSERT_HAT", response.data);
    return response.data;
  },

  async update_hat({ commit }, { id, ...payload }) {
    const response = await axios.put("/hats/" + id, payload);
    commit("UPSERT_HAT", response.data);
    return response.data;
  },

  async delete_hat({ commit }, id) {
    await axios.delete("/hats/" + id);
    commit("REMOVE_HAT", id);
  },

  async assign_user_hats(_, { user_id, hat_ids }) {
    const response = await axios.put("/user/" + user_id + "/hats", { hat_ids });
    return response.data;
  },
};

const mutations = {
  SET_PERMISSIONS(state, permissions) {
    state.permissions = permissions;
  },

  UPSERT_PERMISSION(state, permission) {
    const index = state.permissions.findIndex((p) => p.id === permission.id);
    if (index !== -1) {
      state.permissions.splice(index, 1, permission);
    } else {
      state.permissions.push(permission);
    }
  },

  SET_USER_PERMISSIONS(state, permissions) {
    state.user_permissions = permissions;
    state.user_permissions_loaded = true;
  },

  SET_HATS(state, hats) {
    state.hats = hats;
  },

  UPSERT_HAT(state, hat) {
    const index = state.hats.findIndex((h) => h.id === hat.id);
    if (index !== -1) {
      state.hats.splice(index, 1, hat);
    } else {
      state.hats.push(hat);
    }
  },

  REMOVE_HAT(state, id) {
    state.hats = state.hats.filter((h) => h.id !== id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
